import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Lock, ArrowLeft, CheckCircle, XCircle, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const { language } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [email, setEmail] = useState('');
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const t = {
    fr: {
      title: 'Réinitialiser le mot de passe',
      subtitle: 'Créez un nouveau mot de passe pour votre compte',
      newPassword: 'Nouveau mot de passe',
      confirmPassword: 'Confirmer le mot de passe',
      passwordPlaceholder: 'Minimum 6 caractères',
      submit: 'Réinitialiser',
      submitting: 'Réinitialisation...',
      backToLogin: 'Retour à la connexion',
      successTitle: 'Mot de passe réinitialisé !',
      successMessage: 'Votre mot de passe a été modifié avec succès.',
      loginNow: 'Se connecter maintenant',
      invalidToken: 'Lien invalide ou expiré',
      invalidTokenMessage: 'Ce lien de réinitialisation est invalide ou a expiré. Veuillez demander un nouveau lien.',
      requestNew: 'Demander un nouveau lien',
      verifying: 'Vérification du lien...',
      passwordMismatch: 'Les mots de passe ne correspondent pas',
      passwordTooShort: 'Le mot de passe doit contenir au moins 6 caractères'
    },
    en: {
      title: 'Reset Password',
      subtitle: 'Create a new password for your account',
      newPassword: 'New password',
      confirmPassword: 'Confirm password',
      passwordPlaceholder: 'Minimum 6 characters',
      submit: 'Reset password',
      submitting: 'Resetting...',
      backToLogin: 'Back to login',
      successTitle: 'Password reset!',
      successMessage: 'Your password has been changed successfully.',
      loginNow: 'Login now',
      invalidToken: 'Invalid or expired link',
      invalidTokenMessage: 'This reset link is invalid or has expired. Please request a new link.',
      requestNew: 'Request new link',
      verifying: 'Verifying link...',
      passwordMismatch: 'Passwords do not match',
      passwordTooShort: 'Password must be at least 6 characters'
    }
  };

  const text = t[language] || t.en;

  useEffect(() => {
    if (!token) {
      setVerifying(false);
      setTokenValid(false);
      return;
    }

    const verifyToken = async () => {
      try {
        const response = await fetch(`${API_URL}/api/auth/verify-reset-token/${token}`);
        if (response.ok) {
          const data = await response.json();
          setTokenValid(true);
          setEmail(data.email);
        } else {
          setTokenValid(false);
        }
      } catch (err) {
        setTokenValid(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError(text.passwordTooShort);
      return;
    }

    if (password !== confirmPassword) {
      setError(text.passwordMismatch);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password })
      });

      if (response.ok) {
        setSuccess(true);
      } else {
        const data = await response.json();
        setError(data.detail || 'An error occurred');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#003DA5]/5 to-white p-4">
        <Card className="w-full max-w-md">
          <CardContent className="py-12 text-center">
            <div className="animate-spin w-8 h-8 border-4 border-[#003DA5] border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-muted-foreground">{text.verifying}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Invalid token state
  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#003DA5]/5 to-white p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
              <XCircle className="h-8 w-8 text-red-600" />
            </div>
            <CardTitle className="text-2xl text-red-600">{text.invalidToken}</CardTitle>
            <CardDescription>{text.invalidTokenMessage}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Link to="/forgot-password" className="block">
              <Button className="w-full bg-[#003DA5] hover:bg-[#002d7a]">
                {text.requestNew}
              </Button>
            </Link>
            <Link to="/login" className="block">
              <Button variant="ghost" className="w-full">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {text.backToLogin}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#003DA5]/5 to-white p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl text-green-600">{text.successTitle}</CardTitle>
            <CardDescription>{text.successMessage}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button
              className="w-full bg-[#003DA5] hover:bg-[#002d7a]"
              onClick={() => navigate('/login')}
            >
              {text.loginNow}
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Reset form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#003DA5]/5 to-white p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto w-16 h-16 bg-[#003DA5]/10 rounded-full flex items-center justify-center mb-4">
            <Lock className="h-8 w-8 text-[#003DA5]" />
          </div>
          <CardTitle className="text-2xl">{text.title}</CardTitle>
          <CardDescription>
            {text.subtitle}
            <br />
            <span className="font-medium text-foreground">{email}</span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">{text.newPassword}</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder={text.passwordPlaceholder}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">{text.confirmPassword}</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                placeholder={text.passwordPlaceholder}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                data-testid="reset-password-confirm"
              />
            </div>

            {error && (
              <p className="text-sm text-red-500 text-center">{error}</p>
            )}

            <Button
              type="submit"
              className="w-full bg-[#003DA5] hover:bg-[#002d7a]"
              disabled={loading}
              data-testid="reset-password-submit"
            >
              {loading ? text.submitting : text.submit}
            </Button>

            <Link to="/login" className="block">
              <Button variant="ghost" className="w-full">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {text.backToLogin}
              </Button>
            </Link>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
